import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { formatCurrency } from '../services/budgetService';

// Hot Costs / Estimated Final Cost (EFC) Tracking
function BudgetHotCosts({ budget, expenses, subcategories }) {
  const theme = useTheme();
  
  if (!budget) return null;

  const { totals, lineItems = [] } = budget;

  // Calculate hot costs by category (Movie Magic style)
  const calculateHotCosts = () => {
    const categories = {};
    
    // Initialize categories
    lineItems.forEach(item => {
      // Skip sub-items (they're included in parent totals)
      if (item.parentLineItemId) return;
      
      const subcatInfo = subcategories[item.subcategory];
      const category = subcatInfo?.category || 'OTHER';
      
      if (!categories[category]) {
        categories[category] = {
          budget: 0,
          actual: 0,
          committed: 0, // POs not yet paid
          etc: 0, // Estimate to Complete
          eac: 0, // Estimate at Completion (EFC)
          variance: 0
        };
      }
      
      categories[category].budget += item.budgetAmount || 0;
      categories[category].actual += item.actualAmount || 0;
      categories[category].committed += item.committedAmount || 0;
      
      // ETC: Use override if set, otherwise calculate (budget - actual - committed)
      const itemETC = item.etc !== null && item.etc !== undefined 
        ? item.etc 
        : Math.max(0, (item.budgetAmount || 0) - (item.actualAmount || 0) - (item.committedAmount || 0));
      categories[category].etc += itemETC;
    });

    // Calculate EAC and variance for each category
    Object.keys(categories).forEach(cat => {
      const c = categories[cat];
      // EAC (Estimate at Completion) = Actual + Committed + ETC
      c.eac = c.actual + c.committed + c.etc;
      c.variance = c.budget - c.eac;
    });

    return categories;
  };

  const hotCosts = calculateHotCosts();

  // Overall metrics
  const totalBudget = totals?.grandTotal || 0;
  const totalActual = totals?.actualSpent || 0;
  const totalCommitted = Object.values(hotCosts).reduce((sum, c) => sum + c.committed, 0);
  const totalETC = Object.values(hotCosts).reduce((sum, c) => sum + c.etc, 0);
  const totalEAC = Object.values(hotCosts).reduce((sum, c) => sum + c.eac, 0);
  const totalVariance = totalBudget - totalEAC;
  const percentComplete = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

  const categoryLabels = {
    'ATL': 'Above The Line',
    'BTL_PRODUCTION': 'Below The Line - Production',
    'BTL_POST': 'Below The Line - Post',
    'OTHER': 'Other Costs'
  };

  const getStatusColor = (variance, budget) => {
    const pct = budget > 0 ? (variance / budget) * 100 : 0;
    if (pct < -10) return 'error';
    if (pct < 0) return 'warning';
    return 'success';
  };

  const getStatusIcon = (variance) => {
    if (variance < 0) return <TrendingDownIcon sx={{ fontSize: 16 }} />;
    return <TrendingUpIcon sx={{ fontSize: 16 }} />;
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Original Budget</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatCurrency(totalBudget)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.info.main, 0.05),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Actual to Date</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                {formatCurrency(totalActual)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {percentComplete.toFixed(1)}% of budget
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: alpha(theme.palette.warning.main, 0.05),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">EAC (Estimate at Completion)</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {formatCurrency(totalEAC)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Committed: {formatCurrency(totalCommitted)} | ETC: {formatCurrency(totalETC)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            bgcolor: alpha(totalVariance >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.05),
            border: `1px solid ${alpha(totalVariance >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.2)}`
          }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Projected Variance</Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: totalVariance >= 0 ? 'success.main' : 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {getStatusIcon(totalVariance)}
                {formatCurrency(Math.abs(totalVariance))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalVariance >= 0 ? 'Under budget' : 'Over budget'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Budget Burn Rate
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(totalActual)} / {formatCurrency(totalBudget)}
          </Typography>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, percentComplete)}
            sx={{
              height: 20,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              '& .MuiLinearProgress-bar': {
                bgcolor: percentComplete > 100 ? 'error.main' : 
                         percentComplete > 80 ? 'warning.main' : 'success.main',
                borderRadius: 1
              }
            }}
          />
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
              {percentComplete.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
        {percentComplete > 80 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.main">
              {percentComplete > 100 ? 'Over budget!' : 'Approaching budget limit'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Hot Costs by Category */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Hot Costs by Category
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Budget</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Committed</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actual</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>ETC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>EAC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(hotCosts).map(([catKey, data]) => {
                const pctSpent = data.budget > 0 ? (data.actual / data.budget) * 100 : 0;
                const status = getStatusColor(data.variance, data.budget);
                
                return (
                  <TableRow key={catKey} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {categoryLabels[catKey] || catKey}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(data.budget)}</TableCell>
                    <TableCell align="right" sx={{ color: data.committed > 0 ? 'warning.main' : 'text.secondary' }}>
                      {formatCurrency(data.committed)}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(data.actual)}</TableCell>
                    <TableCell align="right" sx={{ color: 'info.main' }}>
                      {formatCurrency(data.etc)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {formatCurrency(data.eac)}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        color: data.variance >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 600
                      }}
                    >
                      {data.variance >= 0 ? '+' : ''}{formatCurrency(data.variance)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={data.variance >= 0 ? <CheckIcon /> : <WarningIcon />}
                        label={data.variance >= 0 ? 'On Track' : 'At Risk'}
                        color={status}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Total Row */}
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    TOTAL
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCurrency(totalBudget)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {formatCurrency(totalCommitted)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCurrency(totalActual)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'info.main' }}>
                  {formatCurrency(totalETC)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCurrency(totalEAC)}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    color: totalVariance >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 700
                  }}
                >
                  {totalVariance >= 0 ? '+' : ''}{formatCurrency(totalVariance)}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    icon={totalVariance >= 0 ? <CheckIcon /> : <WarningIcon />}
                    label={totalVariance >= 0 ? 'On Track' : 'At Risk'}
                    color={getStatusColor(totalVariance, totalBudget)}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>ETC</strong> = Estimate to Complete (remaining cost)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>EAC</strong> = Estimate at Completion (Actual + Committed + ETC)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>Variance</strong> = Budget - EAC (positive = under budget)
        </Typography>
      </Box>
    </Box>
  );
}

export default BudgetHotCosts;
